/* eslint-disable complexity */
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { formatCurrency } from '@futrli/localisation';
import { PerformanceMetricsBusinessInsightsComparisonData } from '@futrli-sdk/predict';

import * as styles from '../content.scss';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';

interface Props {
  periodData: PerformanceMetricsBusinessInsightsComparisonData;
}

export const BusinessInsightsInfoList = ({ periodData }: Props) => {
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();

  return (
    <ul className={styles.bulletList}>
      {/* Sales */}
      <li>
        <FormattedMessage
          id="onboarding.sales_are_value"
          defaultMessage="Sales are {currValue}."
          values={{
            currValue: (
              <span className={styles.boldText}>
                {formatCurrency(+periodData.sales.value, intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />{' '}
        {Number(periodData.sales.variance) === 0 ? (
          <FormattedMessage
            id="onboarding.difference_value"
            defaultMessage="Difference {value}"
            values={{
              value: <span>{formatCurrency(Number(periodData.sales.variance), intl, currency || '', 0, 0)}</span>
            }}
          />
        ) : (
          <FormattedMessage
            id="onboarding.direction_by_value"
            defaultMessage="{direction} by {changedValue}"
            values={{
              direction:
                Number(periodData.sales.variance) > 0
                  ? intl.formatMessage({ id: 'common.up', defaultMessage: 'Up' })
                  : intl.formatMessage({ id: 'common.down', defaultMessage: 'Down' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.sales.variance) > 0,
                    [styles.negativeChange]: Number(periodData.sales.variance) < 0
                  })}
                >
                  {`${Number(periodData.sales.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.sales.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Costs */}
      <li>
        <FormattedMessage
          id="onboarding.costs_are_value"
          defaultMessage="Costs are {currValue}."
          values={{
            currValue: (
              <span className={styles.boldText}>
                {formatCurrency(+periodData.cost_of_sales.value, intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />{' '}
        {Number(periodData.cost_of_sales.variance) === 0 ? (
          <FormattedMessage
            id="onboarding.difference_value"
            defaultMessage="Difference {value}"
            values={{
              value: (
                <span>{formatCurrency(Number(periodData.cost_of_sales.variance), intl, currency || '', 0, 0)}</span>
              )
            }}
          />
        ) : (
          <FormattedMessage
            id="onboarding.direction_by_value"
            defaultMessage="{direction} by {changedValue}"
            values={{
              direction:
                Number(periodData.cost_of_sales.variance) > 0
                  ? intl.formatMessage({ id: 'common.up', defaultMessage: 'Up' })
                  : intl.formatMessage({ id: 'common.down', defaultMessage: 'Down' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.cost_of_sales.variance) < 0,
                    [styles.negativeChange]: Number(periodData.cost_of_sales.variance) > 0
                  })}
                >
                  {`${Number(periodData.cost_of_sales.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.cost_of_sales.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Expenses */}
      <li>
        <FormattedMessage
          id="onboarding.expenses_are_value"
          defaultMessage="Expenses are {currValue}. "
          values={{
            currValue: (
              <span className={styles.boldText}>
                {formatCurrency(+periodData.expenses.value, intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />{' '}
        {Number(periodData.expenses.variance) === 0 ? (
          <FormattedMessage
            id="onboarding.difference_value"
            defaultMessage="Difference {value}"
            values={{
              value: <span>{formatCurrency(Number(periodData.expenses.variance), intl, currency || '', 0, 0)}</span>
            }}
          />
        ) : (
          <FormattedMessage
            id="onboarding.direction_by_value"
            defaultMessage="{direction} by {changedValue}"
            values={{
              direction:
                Number(periodData.expenses.variance) > 0
                  ? intl.formatMessage({ id: 'common.up', defaultMessage: 'Up' })
                  : intl.formatMessage({ id: 'common.down', defaultMessage: 'Down' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.expenses.variance) < 0,
                    [styles.negativeChange]: Number(periodData.expenses.variance) > 0
                  })}
                >
                  {`${Number(periodData.expenses.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.expenses.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Net profit */}
      <li>
        <FormattedMessage
          id="onboarding.net_profit_is_value"
          defaultMessage="Net profit is {currValue}."
          values={{
            currValue: (
              <span className={styles.boldText}>
                {formatCurrency(+periodData.net_profit.value, intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />{' '}
        {Number(periodData.net_profit.variance) === 0 ? (
          <FormattedMessage
            id="onboarding.difference_value"
            defaultMessage="Difference {value}"
            values={{
              value: <span>{formatCurrency(Number(periodData.net_profit.variance), intl, currency || '', 0, 0)}</span>
            }}
          />
        ) : (
          <FormattedMessage
            id="onboarding.direction_by_value"
            defaultMessage="{direction} by {changedValue}"
            values={{
              direction:
                Number(periodData.net_profit.variance) > 0
                  ? intl.formatMessage({ id: 'common.up', defaultMessage: 'Up' })
                  : intl.formatMessage({ id: 'common.down', defaultMessage: 'Down' }),
              changedValue: (
                <span
                  className={classNames(styles.boldText, {
                    [styles.positiveChange]: Number(periodData.net_profit.variance) > 0,
                    [styles.negativeChange]: Number(periodData.net_profit.variance) < 0
                  })}
                >
                  {`${Number(periodData.net_profit.variance) > 0 ? '+' : ''}${formatCurrency(
                    Number(periodData.net_profit.variance),
                    intl,
                    currency || '',
                    0,
                    0
                  )}`}
                </span>
              )
            }}
          />
        )}
      </li>

      {/* Cash */}
      <li>
        <FormattedMessage
          id="onboarding.available_cash_bank_today_value"
          defaultMessage="Available cash in the bank today is {currValue}"
          values={{
            currValue: (
              <span className={styles.boldText}>
                {formatCurrency(+periodData.cash.value, intl, currency || '', 0, 0)}
              </span>
            )
          }}
        />
      </li>
    </ul>
  );
};
